/**
 * Pivot Points Calculator
 *
 * Calculates support and resistance levels based on previous period's High, Low, Close.
 * Used for identifying potential reversal points and key price levels.
 *
 * Algorithm (per ChartIQ lines 1762-1860):
 * 1. Track previous period's High, Low, Close (period = day by default)
 * 2. Calculate Pivot Point: PP = (High + Low + Close) / 3
 * 3. Calculate Resistance/Support levels using one of two methods:
 *
 * Standard Formula:
 *   R1 = 2*PP - Low
 *   R2 = PP + (High - Low)
 *   R3 = High + 2*(PP - Low)
 *   S1 = 2*PP - High
 *   S2 = PP - (High - Low)
 *   S3 = Low - 2*(High - PP)
 *
 * Fibonacci Formula:
 *   R1 = PP + 0.382*(High - Low)
 *   R2 = PP + 0.618*(High - Low)
 *   R3 = PP + (High - Low)
 *   S1 = PP - 0.382*(High - Low)
 *   S2 = PP - 0.618*(High - Low)
 *   S3 = PP - (High - Low)
 *
 * Key characteristics:
 * - Pivots remain constant until period boundary (typically daily)
 * - Requires previous period's complete HLC data
 * - 7 horizontal lines: PP, R1, R2, R3, S1, S2, S3
 * - Continuous mode recalculates every bar
 *
 * Reference: ChartIQ calculatePivotPoints (calcsChartIQ.js lines 1762-1860)
 */

import { isNotDefined } from "../utils";

const defaultOptions = {
  type: "standard", // "standard" or "fibonacci"
  continuous: false, // If true, recalculate every bar
  period: "auto", // Always auto-detect: daily (≤15min), weekly (15min-1D), monthly (1D+)
};

export default function pivotPoints() {
  let options = defaultOptions;

  /**
   * Detect the bar interval by sampling the first few bars
   */
  function detectBarInterval(data) {
    if (!data || data.length < 2) return 0;

    // Sample first 5 bars to get average interval
    let totalInterval = 0;
    let count = 0;
    const samplesToCheck = Math.min(5, data.length - 1);

    for (let i = 0; i < samplesToCheck; i++) {
      const curr = data[i + 1];
      const prev = data[i];
      if (curr.date && prev.date) {
        const currTime = new Date(curr.date).getTime();
        const prevTime = new Date(prev.date).getTime();
        const interval = currTime - prevTime;
        if (interval > 0) {
          totalInterval += interval;
          count++;
        }
      }
    }

    return count > 0 ? totalInterval / count : 0;
  }

  function calculator(data) {
    const { type, continuous } = options;
    let { period } = options;

    // Safety check for null/undefined data
    if (!data || data.length === 0) {
      return [];
    }

    // Always auto-detect period based on bar interval (period defaults to "auto")
    if ((!period || period === "auto") && data.length >= 2) {
      const barInterval = detectBarInterval(data);
      if (barInterval <= 15 * 60 * 1000) {
        // Up to 15 minutes: use daily pivots
        period = "daily";
      } else if (barInterval < 24 * 60 * 60 * 1000) {
        // 15 minutes to < 1 day: use weekly pivots
        period = "weekly";
      } else {
        // 1 day or more: use monthly pivots
        period = "monthly";
      }
    }

    // Need at least 2 bars (current + previous)
    if (data.length < 2) {
      return data.map(() => undefined);
    }

    const results = [];

    // State to track period boundaries
    let pointers = {
      pivotPoint: NaN,
      high: 0,
      low: 0,
      prevHigh: 0,
      prevLow: 0,
      prevClose: 0,
      hlSpread: 0,
    };

    function resetPivots(i) {
      // Use previous bar's close and the accumulated high/low
      pointers.pivotPoint =
        (pointers.high + pointers.low + data[i - 1].close) / 3;
      pointers.prevHigh = pointers.high;
      pointers.prevLow = pointers.low;
      pointers.prevClose = data[i - 1].close;
      pointers.hlSpread = pointers.high - pointers.low;
      pointers.high = 0;
      pointers.low = 0;
    }

    function calculateLevels() {
      if (isNaN(pointers.pivotPoint) || !isFinite(pointers.pivotPoint)) {
        return {
          pivot: undefined,
          r1: undefined,
          r2: undefined,
          r3: undefined,
          s1: undefined,
          s2: undefined,
          s3: undefined,
        };
      }

      const pp = pointers.pivotPoint;
      const hlSpread = pointers.hlSpread;
      const prevHigh = pointers.prevHigh;
      const prevLow = pointers.prevLow;

      if (type === "fibonacci") {
        return {
          pivot: pp,
          r1: pp + 0.382 * hlSpread,
          r2: pp + 0.618 * hlSpread,
          r3: pp + hlSpread,
          s1: pp - 0.382 * hlSpread,
          s2: pp - 0.618 * hlSpread,
          s3: pp - hlSpread,
        };
      } else {
        // Standard formula
        return {
          pivot: pp,
          r1: 2 * pp - prevLow,
          r2: pp + hlSpread,
          r3: prevHigh + 2 * (pp - prevLow),
          s1: 2 * pp - prevHigh,
          s2: pp - hlSpread,
          s3: prevLow - 2 * (prevHigh - pp),
        };
      }
    }

    // First bar has no data
    results.push(undefined);

    for (let i = 1; i < data.length; i++) {
      const d = data[i];
      const prevD = data[i - 1];

      // Check for required data
      if (
        isNotDefined(d.high) ||
        isNotDefined(d.low) ||
        isNotDefined(d.close) ||
        isNotDefined(prevD.high) ||
        isNotDefined(prevD.low) ||
        isNotDefined(prevD.close)
      ) {
        results.push(undefined);
        continue;
      }

      // Update accumulating high/low for current period
      pointers.high = Math.max(pointers.high, prevD.high);
      pointers.low =
        pointers.low > 0 ? Math.min(pointers.low, prevD.low) : prevD.low;

      // Determine if we should reset pivots
      let shouldReset = false;

      if (continuous) {
        // Continuous mode: recalculate every bar
        shouldReset = true;
      } else if (d.date && prevD.date) {
        const currentDate = new Date(d.date);
        const prevDate = new Date(prevD.date);

        if (period === "monthly") {
          // Monthly period: check if month changed
          if (
            currentDate.getMonth() !== prevDate.getMonth() ||
            currentDate.getFullYear() !== prevDate.getFullYear()
          ) {
            shouldReset = true;
          }
        } else if (period === "weekly") {
          // Weekly period: check if week changed (using ISO week)
          const getWeek = (date) => {
            const d = new Date(date);
            d.setHours(0, 0, 0, 0);
            d.setDate(d.getDate() + 4 - (d.getDay() || 7));
            const yearStart = new Date(d.getFullYear(), 0, 1);
            const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
            return d.getFullYear() * 100 + weekNo;
          };
          if (getWeek(currentDate) !== getWeek(prevDate)) {
            shouldReset = true;
          }
        } else {
          // Daily period: check if day changed
          if (
            currentDate.getDate() !== prevDate.getDate() ||
            currentDate.getMonth() !== prevDate.getMonth() ||
            currentDate.getFullYear() !== prevDate.getFullYear()
          ) {
            shouldReset = true;
          }
        }
      }

      if (shouldReset) {
        resetPivots(i);
      }

      // Calculate and store pivot levels
      results.push(calculateLevels());
    }

    return results;
  }

  /**
   * Returns the number of undefined values at the start
   */
  calculator.undefinedLength = function () {
    // First bar is always undefined (needs previous data)
    return 1;
  };

  /**
   * Gets or sets calculator options
   */
  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
